import type { ColDef, ValueGetterParams, ValueParserParams } from "@ag-grid-community/core";
import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { noop } from "@regrello/core-utils";
import { FeatureFlagKey } from "@regrello/feature-flags-api";
import {
  type FieldInstanceFields,
  FormConstraintConditionOperator,
  type FormFieldFields,
  PropertyDataType,
  type PropertyTypeFields,
  type SpectrumFieldValidationTypeFields,
  type SpectrumValueConstraintFields,
} from "@regrello/graphql-api";
import type { ReactNode } from "react";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

import { FeatureFlagService } from "../../../../../services/FeatureFlagService";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../views/modals/formDialogs/spectrumFields/_internal/ConfigureSpectrumFieldForm";
import type { CustomFieldPlugin } from "../../../customFields/plugins/types/CustomFieldPlugin";
import { RegrelloFormFieldNumber } from "../../../formFields/RegrelloFormFieldNumber";
import {
  type ProspectiveSpectrumFieldVersion,
  SpectrumFieldPluginDecorator,
} from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { FrontendValueConstraintRuleName } from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellNumber } from "./_internal/RegrelloEditableCellNumber";
import { SpectrumNumberValueConstraints } from "./_internal/SpectrumNumberValueConstraints";

type NumberFieldPluginFrontendValue = number | null;

export class SpectrumNumberFieldPluginDecorator<
  T extends NumberFieldPluginFrontendValue,
> extends SpectrumFieldPluginDecorator<T> {
  constructor(plugin: CustomFieldPlugin<T>) {
    super(plugin);
    this.uri = "com.regrello.spectrumField.number";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.RAW;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    if (FeatureFlagService.isEnabled(FeatureFlagKey.NUMBER_AND_CURRENCY_FIELD_IMPROVEMENTS_2025_03)) {
      // (clewis): We may invoke this function ^ while editing a legacy Int-valued field when the FF
      // is enabled. When sending this property type to the backend, we also happen to send the
      // spectrumFieldUUID, which is used to look up the actual property type in the database. Thus
      // this potential property-type mismatch is unimportant.
      return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.FLOAT);
    }
    return propertyTypes.find((propertyType) => propertyType.dataType === PropertyDataType.INT);
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find((validationType) => validationType.validationType === SpectrumFieldValidationType.RAW);
  }

  public findValueConstraintsFromLoadedValueConstraints(
    valueConstraints: SpectrumValueConstraintFields[],
    propertyDataType: PropertyDataType | undefined,
  ) {
    const isNumberAndCurrencyFieldImprovementsEnabled = FeatureFlagService.isEnabled(
      FeatureFlagKey.NUMBER_AND_CURRENCY_FIELD_IMPROVEMENTS_2025_03,
    );
    const isLegacyIntValuedField = propertyDataType === PropertyDataType.INT;
    return valueConstraints
      .filter(
        ({ valueConstraintRule }) =>
          (isNumberAndCurrencyFieldImprovementsEnabled && !isLegacyIntValuedField
            ? valueConstraintRule === FrontendValueConstraintRuleName.MAX_FLOAT
            : valueConstraintRule === FrontendValueConstraintRuleName.MAX_INT) ||
          (isNumberAndCurrencyFieldImprovementsEnabled && !isLegacyIntValuedField
            ? valueConstraintRule === FrontendValueConstraintRuleName.MIN_FLOAT
            : valueConstraintRule === FrontendValueConstraintRuleName.MIN_INT) ||
          // Include these even for legacy int-valued fields (we'll want to show them in the UI but
          // disable it with an explanation):
          valueConstraintRule === FrontendValueConstraintRuleName.DECIMAL_PLACES ||
          valueConstraintRule === FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT,
      )
      .sort((a, b) => {
        // Sort order: DECIMAL_PLACES, MIN_INT, MAX_INT
        const order: Partial<Record<FrontendValueConstraintRuleName, number>> = {
          [FrontendValueConstraintRuleName.DECIMAL_PLACES]: 1,
          [FrontendValueConstraintRuleName.DISALLOW_GROUPINGS_FLOAT]: 2,
          [FrontendValueConstraintRuleName.MIN_INT]: 3,
          [FrontendValueConstraintRuleName.MAX_INT]: 4,
          [FrontendValueConstraintRuleName.MIN_FLOAT]: 3,
          [FrontendValueConstraintRuleName.MAX_FLOAT]: 4,
        };

        const aOrder = order[a.valueConstraintRule as FrontendValueConstraintRuleName] ?? 5;
        const bOrder = order[b.valueConstraintRule as FrontendValueConstraintRuleName] ?? 5;

        return aOrder - bOrder;
      });
  }

  public getConstraintConditionOperators() {
    return {
      [FormConstraintConditionOperator.IS_ZERO]: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.IS_NOT_ZERO]: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.GT]: {
        label: t`Is greater than`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.GTE]: {
        label: t`Is greater than or equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.LT]: {
        label: t`Is less than`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.LTE]: {
        label: t`Is less than or equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.EQ]: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      [FormConstraintConditionOperator.NOT]: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public renderPreviewFormField() {
    return <RegrelloFormFieldNumber disabled={true} onChange={noop} size="medium" value={Number.NaN} />;
  }

  public renderValueConstraints(props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return <SpectrumNumberValueConstraints {...props} />;
  }

  public getColumnDef<RowDatum, T>(
    fieldInstance: FieldInstanceFields,
    _formField: FormFieldFields,
    colDefParams: Partial<ColDef<RowDatum, T>>,
  ): ColDef<RowDatum, T> {
    return {
      ...colDefParams,
      cellClass: "regrello-center-aligned-cell",
      cellEditor: RegrelloEditableCellNumber,
      cellEditorParams: {
        fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      valueGetter: (params: ValueGetterParams<RowDatum, T>) => {
        const value =
          params.data != null &&
          params.colDef.colId != null &&
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          (params.data as any)?.[params.colDef.colId];
        return parseValueFromCell(value) as T;
      },
      valueParser: (params: ValueParserParams<RowDatum, T>) => {
        return parseValueFromCell(params.newValue) as T;
      },
    };
  }
}

function parseValueFromCell(value: string | number): number {
  // Parse float first, because this will convert stuff like "10e6" to "10000000".
  const resolvedValue = typeof value === "string" ? Number.parseFloat(value) : value;

  if (Number.isFinite(resolvedValue)) {
    // Then round since number fields currently only accept integers.
    return Math.round(resolvedValue);
  }
  return Number.NaN;
}
